import { useCustom } from "@refinedev/core";
import { Link } from "react-router";

import { Card, CardNote, ErrorBox, Loading, PageHeader } from "@/components/Page";
import { formatMoney, plural, responseRate } from "@/lib/format";
import { fillWeeks, type ResponseRateWeek } from "@/lib/weeks";

interface Dashboard extends Record<string, unknown> {
  orders_today: number;
  revenue_today_gross: number;
  prompts_sent_today: number;
  answers_today: number;
  delivery_failures_today: number;
  stt_queue: number;
  shipment_errors: number;
  payments_stuck: number;
  unassigned_inbound: number;
  interview_calls_stuck: number;
  interview_calls_unreconciled: number;
  response_rate_weeks: ResponseRateWeek[];
}

const TYGODNI_NA_WYKRESIE = 8;

function Tile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-card border border-line bg-surface px-4.5 py-4">
      <p className="text-base text-ink-soft">{label}</p>
      <p className="tabular mt-1 font-display text-2xl text-ink">{value}</p>
      {hint && <p className="mt-1 text-xs leading-relaxed text-ink-soft">{hint}</p>}
    </div>
  );
}

function Alarm({ count, children, to }: { count: number; children: string; to: string }) {
  if (count === 0) return null;
  return (
    <li>
      <Link to={to} className="text-accent underline underline-offset-4 hover:text-accent-dark">
        {children}
      </Link>
    </li>
  );
}

export function DashboardPage() {
  const { query } = useCustom<Dashboard>({ url: "/dashboard", method: "get" });
  const dane = query.data?.data;

  if (query.isLoading) return <Loading />;
  if (query.error) return <ErrorBox error={query.error} />;
  if (!dane) return null;

  const tygodnie = fillWeeks(dane.response_rate_weeks, TYGODNI_NA_WYKRESIE);
  const najwiekszaWysylka = Math.max(1, ...tygodnie.map((week) => week.sent));
  const nicNieWisi =
    dane.payments_stuck +
      dane.shipment_errors +
      dane.unassigned_inbound +
      dane.interview_calls_stuck +
      dane.interview_calls_unreconciled ===
    0;

  return (
    <>
      {/* "Dashboard" jest w słowniku zakazanym (rozdz. 21 docs/product.md). */}
      <PageHeader title="Przegląd" description="Liczby z dzisiaj i to, co czeka na reakcję." />

      <div className="mb-6.5 grid grid-cols-[repeat(auto-fit,minmax(10.625rem,1fr))] gap-3.5">
        <Tile label="Zamówienia dziś" value={String(dane.orders_today)} />
        <Tile
          label="Przychód dziś"
          value={formatMoney(dane.revenue_today_gross)}
          hint="Tylko zamówienia z potwierdzoną płatnością."
        />
        <Tile label="Wysłane pytania" value={String(dane.prompts_sent_today)} />
        <Tile label="Odpowiedzi" value={String(dane.answers_today)} />
        <Tile
          label="Błędy wysyłek"
          value={String(dane.delivery_failures_today)}
          hint="Pytania, których poczta nie przyjęła."
        />
        <Tile
          label="Kolejka transkrypcji"
          value={String(dane.stt_queue)}
          hint="Nagrania czekające na zamianę na tekst."
        />
      </div>

      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,22.5rem),1fr))] gap-4.5">
        <Card title="Wymaga reakcji">
          {nicNieWisi ? (
            <p className="text-base text-ink-soft">Nic nie wisi. Dobry dzień.</p>
          ) : (
            <ul className="flex list-disc flex-col gap-2 pl-4.5 text-base">
              <Alarm count={dane.payments_stuck} to="/zamowienia?status=pending">
                {`${dane.payments_stuck} ${plural(
                  dane.payments_stuck,
                  "płatność wisi",
                  "płatności wiszą",
                  "płatności wisi",
                )} ponad kwadrans bez potwierdzenia`}
              </Alarm>
              <Alarm count={dane.shipment_errors} to="/przesylki?status=error">
                {`${dane.shipment_errors} ${plural(
                  dane.shipment_errors,
                  "przesyłka z błędem",
                  "przesyłki z błędem",
                  "przesyłek z błędem",
                )}`}
              </Alarm>
              <Alarm count={dane.unassigned_inbound} to="/skrzynka">
                {`${dane.unassigned_inbound} ${plural(
                  dane.unassigned_inbound,
                  "wiadomość czeka",
                  "wiadomości czekają",
                  "wiadomości czeka",
                )} na przypisanie`}
              </Alarm>
              {/* Rozmowa, która stoi w "zaplanowana" długo po terminie, znaczy,
                  że zadanie z kolejki nigdy nie ruszyło. Nic nie zawiodło
                  głośno, więc bez tej pozycji nikt by się nie dowiedział. */}
              <Alarm count={dane.interview_calls_stuck} to="/rozmowy?status=scheduled">
                {`${dane.interview_calls_stuck} ${plural(
                  dane.interview_calls_stuck,
                  "rozmowa nie ruszyła",
                  "rozmowy nie ruszyły",
                  "rozmów nie ruszyło",
                )} mimo terminu`}
              </Alarm>
              <Alarm count={dane.interview_calls_unreconciled} to="/rozmowy?status=unknown">
                {`${dane.interview_calls_unreconciled} ${plural(
                  dane.interview_calls_unreconciled,
                  "rozmowa czeka",
                  "rozmowy czekają",
                  "rozmów czeka",
                )} na uzgodnienie z dostawcą`}
              </Alarm>
            </ul>
          )}
        </Card>

        <Card title="Odsetek odpowiedzi, ostatnie 8 tygodni">
          <ul className="flex h-37.5 items-end gap-2">
            {tygodnie.map((week) => {
              const odsetek = responseRate(week.sent, week.answered);
              // Złoto to reszta, która została bez odpowiedzi. Słupki stoją
              // jeden na drugim, więc cała kolumna ma wysokość wysyłek.
              const bezOdpowiedzi = Math.max(0, week.sent - week.answered);
              return (
                <li
                  key={week.week_start}
                  className="flex h-full flex-1 flex-col items-center justify-end gap-1"
                  title={`${week.answered} z ${week.sent}`}
                >
                  <span className="tabular text-xs text-ink-soft">
                    {odsetek === null ? "-" : `${odsetek}%`}
                  </span>
                  <span
                    className="w-full rounded-t-[3px] bg-gold/60"
                    style={{ height: `${(bezOdpowiedzi / najwiekszaWysylka) * 100}%` }}
                  />
                  <span
                    className="w-full rounded-b-[3px] bg-accent-alt"
                    style={{ height: `${(week.answered / najwiekszaWysylka) * 100}%` }}
                  />
                </li>
              );
            })}
          </ul>
          <CardNote>
            Złoto: wysłane pytania. Zieleń: odpowiedzi. Uwaga przy czytaniu - zła dostarczalność
            poczty wygląda w tych danych identycznie jak niskie zaangażowanie.
          </CardNote>
        </Card>
      </div>
    </>
  );
}
